/**
 * GA4-spårning för HubSpot-anmälningsformuläret (nätverksträffar).
 * Två events: registration_start och sign_up.
 * Ingen GTM, ingen Ads-tag, ingen PII.
 */

const FORM_ID = "41493537-a741-4d1e-9b4c-3f6b6680deaf";
const GA4_ID = "G-0TZLDQ8VJB";
const SIGNUP_TYPE = "network_invitation_list";

const START_KEY = `an_reg_start_${FORM_ID}`;
const SUBMIT_KEY_PREFIX = `an_signup_${FORM_ID}_`;

type HsFormEvent = CustomEvent<{ formId?: string; instanceId?: string }>;

const memoryKeys = new Set<string>();

function markOnce(key: string): boolean {
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, "1");
    return true;
  } catch {
    // sessionStorage kan blockeras – fall tillbaka på in-memory
    if (memoryKeys.has(key)) return false;
    memoryKeys.add(key);
    return true;
  }
}


function send(eventName: string, params: Record<string, string>) {
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== "function") return;
  gtag("event", eventName, { send_to: GA4_ID, ...params });
}

function isTargetForm(event: Event): boolean {
  const detail = (event as HsFormEvent).detail;
  return !!detail && detail.formId === FORM_ID;
}

export function trackRegistrationStart(startSource: "cta" | "form_interaction") {
  if (!markOnce(START_KEY)) return;
  send("registration_start", {
    method: "hubspot",
    form_id: FORM_ID,
    signup_type: SIGNUP_TYPE,
    start_source: startSource,
  });
}

function getConversionKey(event: Event): string {
  const detail = (event as HsFormEvent).detail;
  try {
    const api = (window as unknown as {
      HubSpotFormsV4?: { getFormFromEvent?: (e: Event) => { getConversionId?: () => string } };
    }).HubSpotFormsV4;
    const conversionId = api?.getFormFromEvent?.(event)?.getConversionId?.();
    if (conversionId) return `cid_${conversionId}`;
  } catch {
    // ignorera – faller tillbaka nedan
  }
  return `iid_${detail?.instanceId ?? "unknown"}`;
}

function onSubmissionSuccess(event: Event) {
  if (!isTargetForm(event)) return;
  if (!markOnce(SUBMIT_KEY_PREFIX + getConversionKey(event))) return;
  send("sign_up", {
    method: "hubspot",
    form_id: FORM_ID,
    signup_type: SIGNUP_TYPE,
  });
}

let iframeFocusWatching = false;

/** Best-effort: upptäck första fokus i HubSpot-iframen för rätt form-id. */
function watchIframeFocus() {
  if (iframeFocusWatching) return;
  iframeFocusWatching = true;

  const handleBlur = () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (!active || active.tagName !== "IFRAME") return;
      const frame = active as HTMLIFrameElement;
      const container = frame.closest<HTMLElement>(".hs-form-frame");
      const formIdAttr = container?.dataset.formId ?? frame.getAttribute("data-form-id");
      const src = frame.getAttribute("src") ?? "";
      if (formIdAttr === FORM_ID || src.includes(FORM_ID)) {
        trackRegistrationStart("form_interaction");
      }
    }, 0);
  };
  window.addEventListener("blur", handleBlur);
}

let initialized = false;

export function initHubSpotSignupTracking() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("hs-form-event:on-submission:success", onSubmissionSuccess);
  window.addEventListener("hs-form-event:on-ready", (event) => {
    if (!isTargetForm(event)) return;
    watchIframeFocus();
  });
}

/**
 * GA4-konvertering för Mailchimp Double opt-in.
 * Skickas endast på bekräftelsesidan /tack-prenumeration, en gång per session.
 */
export function trackSubscriptionConfirmed() {
  if (!markOnce(SUBMIT_KEY_PREFIX + "mailchimp_confirmed")) return;
  send("sign_up", {
    method: "mailchimp",
    signup_type: SIGNUP_TYPE,
  });
}
